import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignRole, setShowAssignRole] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [roleForm, setRoleForm] = useState({
    role: 'advocate'
  });
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'advocate',
    tenant_id: ''
  });
  const [editForm, setEditForm] = useState({
    email: '',
    name: '',
    role: 'advocate',
    tenant_id: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  // Define roles based on tenant type
  const LEGAL_ROLES = [
    { value: 'advocate', label: 'Advocate' },
    { value: 'reviewer', label: 'Reviewer' },
    { value: 'tenant_admin', label: 'Tenant Admin' }
  ];

  const CS_ROLES = [
    { value: 'cs', label: 'CS (Company Secretary)' },
    { value: 'cs_approver', label: 'CS Approver' },
    { value: 'tenant_admin', label: 'Tenant Admin' }
  ];

  // Get available roles based on selected tenant's type
  const getAvailableRoles = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (!tenant) return LEGAL_ROLES; // Default to legal roles
    return tenant.tenant_type === 'cs' ? CS_ROLES : LEGAL_ROLES;
  };

  // Get the default role for a tenant type
  const getDefaultRole = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (!tenant) return 'advocate';
    return tenant.tenant_type === 'cs' ? 'cs' : 'advocate';
  };

  // Helper function to get user ID - handles different field names from backend
  const getUserId = (user) => {
    return user?.user_id || user?.id || user?.email;
  };

  // Get current user's role from token
  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserRole(payload.role || '');
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      alert('Please login first');
      navigate('/login');
      return;
    }
    fetchUsers();
    fetchTenants();
  }, [token, navigate]);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Tenants fetched:', response.data);
      setTenants(response.data || []);
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${backendUrl}/api/admin/users`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Users fetched:', response.data);
      setUsers(response.data.users || response.data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch users';
      setError(errorMsg);
      if (err.response?.status === 403) {
        alert('Access denied. Tenant admin privileges required.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setError(null);
      const userId = getUserId(selectedUser);
      await axios.post(
        `${backendUrl}/api/admin/users/${userId}/role`,
        { user_id: userId, role: roleForm.role },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert('Role assigned successfully!');
      setShowAssignRole(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Error assigning role:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to assign role';
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await axios.post(
        `${backendUrl}/api/admin/users`,
        createForm,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert(`User created successfully!\n\nLogin Credentials:\nEmail: ${createForm.email}\nPassword: ${createForm.password}\n\nPlease save these credentials!`);
      setShowCreateUser(false);
      setCreateForm({ email: '', password: '', name: '', role: 'advocate', tenant_id: '' });
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create user';
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  const openAssignRole = (user) => {
    setSelectedUser(user);
    setRoleForm({ role: user.role || 'advocate' });
    setShowAssignRole(true);
  };

  const openEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email || '',
      name: user.full_name || user.name || '',
      role: user.role || 'advocate',
      tenant_id: user.tenant_id || ''
    });
    setShowEditUser(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setError(null);
      const userId = getUserId(selectedUser);
      await axios.put(
        `${backendUrl}/api/admin/users/${userId}`,
        editForm,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert('User updated successfully!');
      setShowEditUser(false);
      setSelectedUser(null);
      setEditForm({ email: '', name: '', role: 'advocate', tenant_id: '' });
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to update user';
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user ${user.email || user.full_name}? This action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);
      const userId = getUserId(user);
      await axios.delete(
        `${backendUrl}/api/admin/users/${userId}`,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      alert('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete user';
      setError(errorMsg);
      alert(errorMsg);
    }
  };

  if (error && !users.length) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-800 font-semibold mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => navigate('/admin')}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <div className="space-x-4">
            <button
              onClick={() => setShowCreateUser(!showCreateUser)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {showCreateUser ? 'Cancel' : 'Create User'}
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Admin
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Create User Form */}
        {showCreateUser && (
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h2 className="text-xl font-semibold mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    required
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    placeholder="Enter password"
                  />
                </div>
                {/* Tenant Selection - Must be selected first to determine roles */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={createForm.tenant_id}
                    onChange={(e) => {
                      const newTenantId = e.target.value;
                      setCreateForm({ 
                        ...createForm, 
                        tenant_id: newTenantId,
                        role: getDefaultRole(newTenantId) // Reset role to default for selected tenant type
                      });
                    }}
                    className="w-full px-3 py-2 border rounded"
                    required
                    data-testid="create-user-tenant-select"
                  >
                    <option value="">-- Select Tenant First --</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.tenant_type === 'cs' ? 'CS' : 'Legal'})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Select tenant to see available roles</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    disabled={!createForm.tenant_id}
                    data-testid="create-user-role-select"
                  >
                    {!createForm.tenant_id ? (
                      <option value="">-- Select Tenant First --</option>
                    ) : (
                      getAvailableRoles(createForm.tenant_id).map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))
                    )}
                  </select>
                  {createForm.tenant_id && (
                    <p className="text-xs text-blue-600 mt-1">
                      Roles for {tenants.find(t => t.id === createForm.tenant_id)?.tenant_type === 'cs' ? 'CS' : 'Legal'} tenant
                    </p>
                  )}
                </div>
              </div>
              <button
                type="submit"
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                disabled={!createForm.tenant_id}
              >
                Create User
              </button>
            </form>
          </div>
        )}

        {/* Assign Role Modal */}
        {showAssignRole && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold mb-4">Assign Role to {selectedUser.email}</h2>
              <form onSubmit={handleAssignRole} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={roleForm.role}
                    onChange={(e) => setRoleForm({ role: e.target.value })}
                    className="w-full px-3 py-2 border rounded"
                    data-testid="assign-role-select"
                  >
                    {getAvailableRoles(selectedUser.tenant_id).map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                    {currentUserRole === 'super_admin' && (
                      <option value="super_admin">Super Admin</option>
                    )}
                  </select>
                  <p className="text-xs text-blue-600 mt-1">
                    Showing roles for {tenants.find(t => t.id === selectedUser.tenant_id)?.tenant_type === 'cs' ? 'CS' : 'Legal'} tenant
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Assign Role
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAssignRole(false);
                      setSelectedUser(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUser && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold mb-4">Edit User: {selectedUser.email}</h2>
              <form onSubmit={handleEditUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenant <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editForm.tenant_id}
                    onChange={(e) => {
                      const newTenantId = e.target.value;
                      setEditForm({
                        ...editForm, 
                        tenant_id: newTenantId,
                        role: getDefaultRole(newTenantId) // Reset role when tenant changes
                      });
                    }}
                    className="w-full px-3 py-2 border rounded"
                    required
                    data-testid="edit-user-tenant-select"
                  >
                    <option value="">-- Select Tenant --</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.tenant_type === 'cs' ? 'CS' : 'Legal'})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">⚠️ Changing tenant will reset role options</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editForm.role}
                    onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                    className="w-full px-3 py-2 border rounded"
                    disabled={!editForm.tenant_id}
                    data-testid="edit-user-role-select"
                  >
                    {!editForm.tenant_id ? (
                      <option value="">-- Select Tenant First --</option>
                    ) : (
                      getAvailableRoles(editForm.tenant_id).map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))
                    )}
                  </select>
                  {editForm.tenant_id && (
                    <p className="text-xs text-blue-600 mt-1">
                      Roles for {tenants.find(t => t.id === editForm.tenant_id)?.tenant_type === 'cs' ? 'CS' : 'Legal'} tenant
                    </p>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Update User
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditUser(false);
                      setSelectedUser(null);
                      setEditForm({ email: '', name: '', role: 'advocate', tenant_id: '' });
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* Users List */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="p-6 text-center">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <p>No users found in this tenant.</p>
              <p className="text-sm mt-2">Users will appear here after they login for the first time.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  {currentUserRole === 'super_admin' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => {
                  const userTenant = tenants.find(t => t.id === user.tenant_id);
                  return (
                  <tr key={getUserId(user)}>
                    <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{user.name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded ${
                        user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                        user.role === 'tenant_admin' ? 'bg-purple-100 text-purple-800' :
                        user.role === 'reviewer' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role?.replace('_', ' ') || 'advocate'}
                      </span>
                    </td>
                    {currentUserRole === 'super_admin' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                          {userTenant?.name || user.tenant_id || 'default'}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex space-x-3">
                        <button
                          onClick={() => openEditUser(user)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openAssignRole(user)}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                        >
                          Change Role
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
